import { useEffect, useRef, type CSSProperties } from "react";
import ChatHeader from "./components/ChatHeader";
import ChatInput from "./components/ChatInput";
import MessageList from "./components/MessageList";
import PanelLoadingShimmer from "./components/PanelLoadingShimmer";
import PopularQuestions from "./components/PopularQuestions";
import MobileChat from "./components/mobile/MobileChat";
import {
  useAvatarWidget,
  type AvatarWidgetController,
} from "./hooks/useAvatarWidget";
import { tokens } from "./theme";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100vh",
    background: tokens.surface.canvas,
    fontFamily: tokens.font.family,
    color: tokens.text.primary,
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.space[4],
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[3],
    background: tokens.surface.canvas,
  },
} as const satisfies Record<string, CSSProperties>;

export default function App() {
  const widget = useAvatarWidget();

  if (widget.channel === "mobile_webview") {
    return <MobileChat widget={widget} />;
  }

  return <WebChat widget={widget} />;
}

function WebChat({ widget }: { widget: AvatarWidgetController }) {
  const {
    isOpen,
    isPanelReady,
    messages,
    isStreaming,
    bootstrapping,
    lastFailedText,
    widgetConfig,
    greetingText,
    features,
    popularQuestions,
    showPopular,
    inputDisabled,
    sendMessage,
    attachments,
    addAttachments,
    removeAttachment,
    resetConversation,
  } = widget;

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  if (!isOpen) return null;

  if (!isPanelReady) {
    return <PanelLoadingShimmer />;
  }

  return (
    <div style={styles.root}>
      <ChatHeader
        onReset={() => {
          void resetConversation();
        }}
        disabled={isStreaming || bootstrapping}
        name={widgetConfig?.name}
      />
      <div ref={scrollRef} style={styles.body}>
        {/* pre_chat_form is honored as a flag; dedicated form UI ships later. */}
        {showPopular && (
          <PopularQuestions
            onSelectQuestion={sendMessage}
            disabled={inputDisabled}
            greeting={greetingText}
            questions={popularQuestions}
          />
        )}

        <MessageList
          messages={messages}
          lastFailedText={lastFailedText}
          onRetry={sendMessage}
        />
      </div>
      <ChatInput
        onSend={sendMessage}
        disabled={inputDisabled}
        showPoweredBy={widgetConfig?.branding.show_powered_by !== false}
        features={features}
        attachments={attachments}
        onAttach={addAttachments}
        onRemoveAttachment={removeAttachment}
      />
    </div>
  );
}
